import { getCurrentInstance } from "vue";
import { useRouter } from "vue-router";
import { PostHog } from "posthog-js";
import { useUserStore } from "@/stores/user";
import { User } from "@/types/user-types";
import { Company } from "@/types/company-types";

/**
 * Shared company-user session hydration, analytics, and post-login routing used by
 * HomeView (Frontegg) and cognito/ImpersonateView.
 */
export function useCompanyAuthSession() {
  const router = useRouter();
  const userStore = useUserStore();
  const posthog = (
    getCurrentInstance()?.proxy as { $posthog: PostHog } | undefined
  )?.$posthog;

  function applyCompanyUserToStore(
    sessionEmail: string,
    user: User,
    company: Company,
    token: string,
    addSubscription: boolean | null,
    customPayoutFee: number | null
  ) {
    if (userStore.firstName !== user.firstName) {
      userStore.setFirstName(user.firstName);
    }
    userStore.setLastName(user.lastName);
    userStore.setTitle(user.title);
    userStore.setToken(token);
    userStore.setEmail(sessionEmail);
    userStore.setUserType("company_user");
    userStore.setSwitchable(user.switchable);
    userStore.setCompanyName(company.name);
    userStore.setCompanyId(company.id || "");
    userStore.setCompanyWebsite(company.website);
    userStore.setAddSubscription(addSubscription);
    userStore.setCustomPayoutFee(customPayoutFee);
    userStore.setProgramLinkedinUrl(company.program_linkedin_url);
  }

  function postAuthRedirection(
    hasCompany: boolean,
    personalInfoRequired: boolean
  ) {
    if (!hasCompany || personalInfoRequired) {
      const pendingCompanyDetails = !hasCompany;
      void router.push({
        name: "AddCompanyDetails",
        query: { pendingCompanyDetails: pendingCompanyDetails.toString() },
      });
    } else {
      void router.push({ name: "SignInLanding" });
    }
  }

  function identifyCompanyUserProduktly(uuid: string, email: string) {
    if (window.Produktly) {
      window.Produktly.identifyUser(uuid, {
        email,
      });
    }
  }

  /**
   * Same PostHog capture + identify as HomeView after login. Call after
   * `applyCompanyUserToStore` so `userStore.email` and company fields are set.
   * PostHog is captured when the composable runs (setup), so this is safe from async flows.
   */
  function notifyCompanyUserPosthog(personalInfoRequired: boolean) {
    const sessionEmail = userStore.email;
    if (!posthog || !sessionEmail) {
      return;
    }

    if (personalInfoRequired) {
      posthog.capture("Company_User_Clicked_On_Invite_Signup", {
        email: sessionEmail,
      });
    } else {
      posthog.capture("Company_User_Clicked_Signup", {
        email: sessionEmail,
      });
    }

    posthog.identify(sessionEmail, {
      email: sessionEmail,
      company_email: sessionEmail,
      expert_user: false,
      company_user: true,
      attendee_user: false,
      company_first: userStore.firstName,
      company_last: userStore.lastName,
      company_name: userStore.companyName,
      company_website: userStore.companyWebsite,
    });
  }

  return {
    userStore,
    applyCompanyUserToStore,
    postAuthRedirection,
    identifyCompanyUserProduktly,
    notifyCompanyUserPosthog,
  };
}
